import { useState, useEffect, useCallback, useRef } from 'react';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { jwtDecode } from 'jwt-decode';
import fetchWithAuth from '../fetchWithAuth';
import { toast } from 'react-toastify';
import logger from '../utils/logger.js';

const useAuth = (setView) => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [showDeactivatedPrompt, setShowDeactivatedPrompt] = useState(false);
  const [showSignupConfirmation, setShowSignupConfirmation] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const inactivityTimeoutRef = useRef(null);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('memberToken');
    localStorage.removeItem('dealsCache');
    setUser(null);
    setView('landing');
    setError('');
    setPasswordError('');
    setLoading(false); // Reset loading state
    setIsSessionExpired(false);
    setShowTimeoutWarning(false);
    logger.debug('User logged out');
  }, [setView]);

  const handleSessionExpired = useCallback(() => {
    if (!isSessionExpired) {
      setIsSessionExpired(true);
      handleLogout();
      toast.warn('Your session has expired. Please log in again.');
    }
  }, [isSessionExpired, handleLogout]);

  const verifyToken = useCallback(async (token) => {
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp * 1000 < Date.now()) throw new Error('Token expired');
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/validate-token`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }, handleSessionExpired, 10000);
      const data = await response.json();
      logger.debug('Token validated, user data:', data);

      let orgData = null;
      try {
        const orgId = data.organizationId?._id?.toString() || data.organizationId?.toString();

        // ─────────────────────────────────────────────────────────────
        // FIXED: Skip org fetch for promoters (they have no organizationId)
        // Added ~5 lines – fully reversible by commenting out this block
        // Prevents "Invalid organizationId" error and allows login to complete
        // ─────────────────────────────────────────────────────────────
        if (data.role === 'Promoter' || !orgId) {
          logger.debug('Promoter or no orgId — skipping organization fetch');
          orgData = null;
        } else {
          if (!orgId) throw new Error('Invalid organizationId');
          logger.debug('Fetching organization for ID:', orgId);
          const orgResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/organizations/${orgId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }, handleSessionExpired, 10000);
          orgData = await orgResponse.json();
          logger.debug('Organization data:', orgData);
        }
        // ─────────────────────────────────────────────────────────────

      } catch (orgErr) {
        logger.error('Failed to fetch organization:', orgErr.message);
        if (orgErr.message === 'Session expired') throw orgErr;
        if (data.email === 'admin@scover.co.za' && data.role === 'SuperAdmin') {
          logger.warn('SuperAdmin login continuing without organization data');
          return { ...data, organization: null };
        }
        throw orgErr;
      }

      return { ...data, organization: orgData };
    } catch (err) {
      logger.error('Token verification error:', err.message);
      if (err.message !== 'Session expired') {
        localStorage.removeItem('memberToken');
      }
      return null;
    }
  }, [handleSessionExpired]);

  const refreshToken = useCallback(async () => {
    const token = localStorage.getItem('memberToken');
    if (!token) return false;

    try {
      const decoded = jwtDecode(token);
      const timeToExpiry = decoded.exp * 1000 - Date.now();
      if (timeToExpiry < 5 * 60 * 1000) {
        logger.debug('Token nearing expiry, refreshing...');
        const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/refresh-token`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }, handleSessionExpired, 10000);
        const data = await response.json();
        logger.debug('Token refreshed:', data.token);
        localStorage.setItem('memberToken', data.token);
        const userData = await verifyToken(data.token);
        if (userData) {
          setUser({ ...userData, token: data.token });
        }
        return true;
      }
      return false;
    } catch (err) {
      logger.error('Token refresh error:', err.message);
      handleSessionExpired();
      return false;
    }
  }, [handleSessionExpired, verifyToken]);

  const resetInactivityTimer = useCallback(() => {
    logger.debug('Resetting inactivity timer');
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    if (user && !showTimeoutWarning) {
      inactivityTimeoutRef.current = setTimeout(() => {
        logger.debug('Inactivity timeout triggered, showing warning');
        setShowTimeoutWarning(true);
      }, 18 * 60 * 1000); // 18 minutes
    }
  }, [user, showTimeoutWarning]);

  const handleStayLoggedIn = useCallback(() => {
    logger.debug('User chose to stay logged in');
    setShowTimeoutWarning(false);
    resetInactivityTimer();
    refreshToken();
  }, [resetInactivityTimer, refreshToken]);

  const handleInactivityLogout = useCallback(() => {
    logger.debug('Inactivity logout triggered');
    setShowTimeoutWarning(false);
    handleLogout();
  }, [handleLogout]);

  const handleLogin = async (username, password) => {
    setLoading(true);
    setError('');
    setPasswordError('');
    logger.debug('handleLogin called with:', username);
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      }, handleSessionExpired, 10000);
      const data = await response.json();
      logger.debug('Login success, token:', data.token);
      localStorage.setItem('memberToken', data.token);
      logger.debug('Fetching member data...');
      const memberResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/me`, {
        headers: { 'Authorization': `Bearer ${data.token}` },
      }, handleSessionExpired, 10000);
      const memberData = await memberResponse.json();
      logger.debug('Member data fetched:', memberData);

      let orgData = null;
      try {
        const orgId = memberData.organizationId?._id?.toString() || memberData.organizationId?.toString();

        // ─────────────────────────────────────────────────────────────
        // FIXED: Skip org fetch for promoters (they have no organizationId)
        // Added ~5 lines – fully reversible by commenting out this block
        // Prevents "Invalid organizationId" error and allows login to complete
        // ─────────────────────────────────────────────────────────────
        if (memberData.role === 'Promoter' || !orgId) {
          logger.debug('Promoter or no orgId — skipping organization fetch');
          orgData = null;
        } else {
          if (!orgId) throw new Error('Invalid organizationId');
          logger.debug('Fetching organization for ID:', orgId);
          const orgResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/organizations/${orgId}`, {
            headers: { 'Authorization': `Bearer ${data.token}` },
          }, handleSessionExpired, 10000);
          orgData = await orgResponse.json();
          logger.debug('Organization data:', orgData);
        }
        // ─────────────────────────────────────────────────────────────

      } catch (orgErr) {
        logger.error('Failed to fetch organization:', orgErr.message);
        if (orgErr.message === 'Session expired') throw orgErr;
        if (memberData.email === 'admin@scover.co.za' && memberData.role === 'SuperAdmin') {
          logger.warn('SuperAdmin login continuing without organization data');
          setUser({ ...memberData, token: data.token, organization: null });
          setView('superAdmin');
          return;
        }
        throw orgErr;
      }

      setUser({ ...memberData, token: data.token, organization: orgData });
      logger.debug('Setting view based on role and verification status');
      if (memberData.email === 'admin@scover.co.za' && memberData.role === 'SuperAdmin') {
        setView('superAdmin');
      } else if (orgData?.isActive === false || orgData?.verificationStatus !== 'approved') {
        setView('verificationPending');
      } else {
        setView('dashboard');
      }
    } catch (err) {
      if (err.message === 'Session expired') return;
      logger.error('Login error:', err.message);
      if (err.message === 'Invalid password') {
        setPasswordError('Invalid username or password entered.');
        setView('login');
      } else if (err.message === 'Not found') {
        try {
          const checkResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/check-deactivated`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username }),
          }, handleSessionExpired, 10000);
          const checkData = await checkResponse.json();
          if (checkResponse.ok && checkData.deactivated) {
            setShowDeactivatedPrompt(true);
          } else {
            setPasswordError('Invalid username or password entered.');
            setShowSignupPrompt(true);
          }
        } catch (checkErr) {
          logger.error('Check deactivated error:', checkErr.message);
          setPasswordError('Invalid username or password entered.');
          setShowSignupPrompt(true);
        }
        setView('login');
      } else {
        setError(err.message || 'Failed to log in, please try again');
        setView('login');
      }
    } finally {
      logger.debug('Resetting loading state');
      setLoading(false); // Simplified reset
    }
  };

  const handleSignup = async (signupData) => {
    setLoading(true);
    setError('');
    logger.debug('handleSignup called with:', signupData);
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupData),
      }, handleSessionExpired, 10000);
      const data = await response.json();
      logger.debug('Signup success:', data);
      setShowSignupConfirmation(true);
    } catch (err) {
      if (err.message === 'Session expired') return;
      logger.error('Signup error:', err.message);
      setError(err.message || 'Failed to sign up, please try again');
      setView('signup');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    logger.debug('handleGoogleLogin called');
    let googleUser = null;
    try {
      const result = await signInWithPopup(auth, googleProvider);
      googleUser = result.user;
      logger.debug('Google Sign-In user:', googleUser.email);
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: googleUser.email, googleId: googleUser.uid }),
      }, handleSessionExpired, 10000);
      const data = await response.json();
      logger.debug('Google login success, token:', data.token);
      localStorage.setItem('memberToken', data.token);
      logger.debug('Fetching member data...');
      const memberResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/me`, {
        headers: { 'Authorization': `Bearer ${data.token}` },
      }, handleSessionExpired, 10000);
      const memberData = await memberResponse.json();
      logger.debug('Member data fetched:', memberData);

      let orgData = null;
      try {
        const orgId = memberData.organizationId?._id?.toString() || memberData.organizationId?.toString();

        // ─────────────────────────────────────────────────────────────
        // FIXED: Skip org fetch for promoters (they have no organizationId)
        // Added ~5 lines – fully reversible by commenting out this block
        // Prevents "Invalid organizationId" error and allows login to complete
        // ─────────────────────────────────────────────────────────────
        if (memberData.role === 'Promoter' || !orgId) {
          logger.debug('Promoter or no orgId — skipping organization fetch');
          orgData = null;
        } else {
          if (!orgId) throw new Error('Invalid organizationId');
          logger.debug('Fetching organization for ID:', orgId);
          const orgResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/organizations/${orgId}`, {
            headers: { 'Authorization': `Bearer ${data.token}` },
          }, handleSessionExpired, 10000);
          orgData = await orgResponse.json();
          logger.debug('Organization data:', orgData);
        }
        // ─────────────────────────────────────────────────────────────

      } catch (orgErr) {
        logger.error('Failed to fetch organization:', orgErr.message);
        if (orgErr.message === 'Session expired') throw orgErr;
        if (memberData.email === 'admin@scover.co.za' && memberData.role === 'SuperAdmin') {
          logger.warn('SuperAdmin login continuing without organization data');
          setUser({ ...memberData, token: data.token, organization: null });
          setView('superAdmin');
          return;
        }
        throw orgErr;
      }

      setUser({ ...memberData, token: data.token, organization: orgData });
      logger.debug('Setting view based on role and verification status');
      if (memberData.email === 'admin@scover.co.za' && memberData.role === 'SuperAdmin') {
        setView('superAdmin');
      } else if (orgData?.isActive === false || orgData?.verificationStatus !== 'approved') {
        setView('verificationPending');
      } else {
        setView('dashboard');
      }
    } catch (err) {
      if (err.message === 'Session expired') return;
      logger.error('Google login error:', err.message);
      if (err.message === 'Not found') {
        if (googleUser) {
          try {
            const checkResponse = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/check-deactivated`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: googleUser.email }),
            }, handleSessionExpired, 10000);
            const checkData = await checkResponse.json();
            if (checkResponse.ok && checkData.deactivated) {
              setShowDeactivatedPrompt(true);
            } else {
              setShowSignupPrompt(true);
            }
          } catch (checkErr) {
            logger.error('Check deactivated error:', checkErr.message);
            setShowSignupPrompt(true);
          }
        } else {
          setShowSignupPrompt(true);
        }
      } else {
        setError(err.message || 'Failed to log in with Google, please try again');
        setView('login');
      }
    } finally {
      logger.debug('Resetting loading state');
      setLoading(false);
    }
  };

  const handleSignupPromptResponse = (choice) => {
    setShowSignupPrompt(false);
    if (choice === 'signup') setView('signup');
    else setView('login');
  };

  const handleDeactivatedPromptResponse = () => {
    setShowDeactivatedPrompt(false);
    setView('login');
  };

  const handleSignupConfirmationClose = () => {
    setShowSignupConfirmation(false);
    setView('landing');
  };

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (token) {
      verifyToken(token).then((userData) => {
        if (userData) {
          setUser({ ...userData, token });
          // ─────────────────────────────────────────────────────────────
          // FIXED: Auto-redirect promoters to Refer & Earn on login
          // Added ~8 lines – fully reversible by commenting out this block
          // Promoters go to 'promoters' view; others follow original logic
          // ─────────────────────────────────────────────────────────────
          if (userData.role === 'Promoter') {
            setView('promoters');
          } else if (userData.email === 'admin@scover.co.za' && userData.role === 'SuperAdmin') {
            setView('superAdmin');
          } else if (userData.organization && (userData.organization.isActive === false || userData.organization.verificationStatus !== 'approved')) {
            setView('verificationPending');
          } else {
            setView('dashboard');
          }
          // ─────────────────────────────────────────────────────────────
        } else {
          setView('landing');
        }
        setLoading(false);
      });
    } else {
      setView('landing');
      setLoading(false);
    }

    const refreshInterval = setInterval(refreshToken, 60 * 1000);
    return () => clearInterval(refreshInterval);
  }, [verifyToken, setView, refreshToken]);

  useEffect(() => {
    logger.debug('Setting up inactivity listeners, user:', !!user, 'showTimeoutWarning:', showTimeoutWarning);
    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    const resetTimer = () => {
      resetInactivityTimer();
    };

    if (user && !showTimeoutWarning) {
      events.forEach(event => window.addEventListener(event, resetTimer));
      resetInactivityTimer();
    }

    return () => {
      logger.debug('Cleaning up inactivity listeners');
      events.forEach(event => window.removeEventListener(event, resetTimer));
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [user, showTimeoutWarning, resetInactivityTimer]);

  useEffect(() => {
    logger.debug('Timeout warning state changed:', showTimeoutWarning);
    if (showTimeoutWarning) {
      const logoutTimer = setTimeout(() => {
        logger.debug('Executing inactivity logout');
        handleInactivityLogout();
      }, 2 * 60 * 1000); // 2 minutes
      return () => {
        logger.debug('Clearing logout timer');
        clearTimeout(logoutTimer);
      };
    }
  }, [showTimeoutWarning, handleInactivityLogout]);

  return {
    user,
    setUser,
    error,
    setError,
    passwordError,
    loading,
    setLoading,
    handleLogin,
    handleSignup,
    handleGoogleLogin,
    handleLogout,
    showSignupPrompt,
    handleSignupPromptResponse,
    showDeactivatedPrompt,
    handleDeactivatedPromptResponse,
    showSignupConfirmation,
    handleSignupConfirmationClose,
    onSessionExpired: handleSessionExpired,
    showTimeoutWarning,
    handleStayLoggedIn,
    handleInactivityLogout,
  };
};

export default useAuth;